<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { usePanelAppStore } from '@addictivegroup/addictive-permissions'
import { AppSwitcher } from '@addictivegroup/addictive-auth/components'
import { useSession } from '@/stores/session.js'
import { useUi } from '@/stores/ui.js'
import { useDirectory } from '@/stores/directory.js'
import { useViews } from '@/stores/views.js'
import { offline } from '@/lib/offline.js'
import { startNoteUploads } from '@/lib/noteUploads.js'
import { flushLearning, prefetchLocalSpeech } from '@addictivegroup/addictive-dictation'
import { spelling } from '@/lib/spelling.js'
import { formatRelative } from '@/lib/format.js'
import { PANEL_PAGES } from '@/lib/panelPages.js'
import { setLocale } from '@/i18n/index.js'
import { connectEcho, disconnectEcho } from '@/plugins/echo.js'
import { forgetPermissionCache } from '@/lib/permissionsCache.js'
import GlobalSearch from './GlobalSearch.vue'
import SyncIndicator from './SyncIndicator.vue'
import PrivacyToggle from '@/components/privacy/PrivacyToggle.vue'
import CreatePanels from './CreatePanels.vue'
import UnsavedBar from './UnsavedBar.vue'
import UpdatePrompt from './UpdatePrompt.vue'
import UiAvatar from '@/components/ui/UiAvatar.vue'
import UiMenu from '@/components/ui/UiMenu.vue'
import UiNotice from '@/components/ui/UiNotice.vue'
import { installPinchZoom, setTextZoom, textZoom } from '@/lib/textZoom.js'

const session = useSession()
// The person's own words the spelling takes as right (D-208), from their settings — in every text.
watch(() => session.me?.settings?.spelling_words, (words) => spelling.useOwnWords(words), { immediate: true })
const ui = useUi()
const pages = usePanelAppStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const sections = computed(() => [
  {
    title: t('nav.sales'),
    items: [
      { to: { name: 'dashboard' }, icon: 'fa-gauge-high', label: t('nav.dashboard'), page: PANEL_PAGES.Dashboard, exact: true },
      { to: { name: 'work' }, icon: 'fa-list-check', label: t('nav.work'), page: PANEL_PAGES.Work },
      { to: { name: 'list', params: { entityPath: 'accounts' } }, icon: 'fa-building', label: t('nav.accounts'), page: PANEL_PAGES.Accounts, path: '/accounts' },
      { to: { name: 'list', params: { entityPath: 'contacts' } }, icon: 'fa-user', label: t('nav.contacts'), page: PANEL_PAGES.Contacts, path: '/contacts' },
      { to: { name: 'list', params: { entityPath: 'leads' } }, icon: 'fa-bullseye', label: t('nav.leads'), page: PANEL_PAGES.Leads, path: '/leads' },
      { to: { name: 'list', params: { entityPath: 'opportunities' } }, icon: 'fa-sack-dollar', label: t('nav.opportunities'), page: PANEL_PAGES.Opportunities, path: '/opportunities' },
      { to: { name: 'list', params: { entityPath: 'activities' } }, icon: 'fa-calendar-check', label: t('nav.activities'), page: PANEL_PAGES.Activities, path: '/activities' },
      { to: { name: 'list', params: { entityPath: 'notes' } }, icon: 'fa-note-sticky', label: t('nav.notes'), page: PANEL_PAGES.Notes, path: '/notes' },
      { to: { name: 'list', params: { entityPath: 'competitors' } }, icon: 'fa-chess-knight', label: t('nav.competitors'), page: PANEL_PAGES.Competitors, path: '/competitors' },
      // Sales materials to look through, send, attach (D-332): for those who may see them.
      { to: { name: 'materials' }, icon: 'fa-photo-film', label: t('nav.materials'), path: '/materials', permissions: ['sales_material:view', 'sales_material:own_view'] },
    ],
  },
  {
    title: t('nav.marketing'),
    items: [
      { to: { name: 'list', params: { entityPath: 'campaigns' } }, icon: 'fa-bullhorn', label: t('nav.campaigns'), page: PANEL_PAGES.Campaigns, path: '/campaigns' },
      { to: { name: 'list', params: { entityPath: 'marketing-lists' } }, icon: 'fa-address-book', label: t('nav.marketingLists'), page: PANEL_PAGES.MarketingLists, path: '/marketing-lists' },
      { to: { name: 'list', params: { entityPath: 'events' } }, icon: 'fa-calendar-days', label: t('nav.events'), page: PANEL_PAGES.Events, path: '/events' },
      // Sales materials kept (D-332): for those who make them.
      { to: { name: 'list', params: { entityPath: 'sales-materials' } }, icon: 'fa-photo-film', label: t('nav.salesMaterials'), path: '/sales-materials', permissions: ['sales_material:create', 'sales_material:update', 'sales_material:own_update'] },
    ],
  },
  {
    title: t('nav.offerBuilder'),
    items: [
      { to: { name: 'list', params: { entityPath: 'offers' } }, icon: 'fa-file-signature', label: t('nav.offers'), page: PANEL_PAGES.Offers, path: '/offers' },
      { to: { name: 'list', params: { entityPath: 'contracts' } }, icon: 'fa-file-contract', label: t('nav.contracts'), page: PANEL_PAGES.Contracts, path: '/contracts' },
      { to: { name: 'list', params: { entityPath: 'agreements' } }, icon: 'fa-handshake', label: t('nav.agreements'), page: PANEL_PAGES.Agreements, path: '/agreements' },
      { to: { name: 'catalogue' }, icon: 'fa-boxes-stacked', label: t('nav.catalogue'), page: PANEL_PAGES.Catalogue, exact: true },
      { to: { name: 'templates' }, icon: 'fa-layer-group', label: t('nav.templates'), page: PANEL_PAGES.OfferTemplates },
    ],
  },
  {
    title: t('nav.admin'),
    items: [
      { to: { name: 'reports' }, icon: 'fa-chart-column', label: t('nav.reports'), page: PANEL_PAGES.Reports },
      { to: { name: 'settings' }, icon: 'fa-gear', label: t('nav.settings'), settings: true, path: '/settings' },
    ],
  },
])

const SETTINGS_PAGES = Object.entries(PANEL_PAGES).filter(([k]) => k.startsWith('Settings')).map(([, v]) => v)
// Nothing is offered before the page grants are known — no flash of screens that then vanish.
function allowed(item) {
  if (!pages.loaded) return false
  if (item.settings) return SETTINGS_PAGES.some((p) => pages.canViewPage(p))
  if (item.permissions) return item.permissions.some((p) => session.has(p))
  return !item.page || pages.canViewPage(item.page)
}
// The phone bar: the four most used screens, of those this person may open.
const phoneItems = computed(() => [
  { to: { name: 'work' }, icon: 'fa-list-check', label: t('nav.work'), page: PANEL_PAGES.Work },
  { to: { name: 'list', params: { entityPath: 'accounts' } }, icon: 'fa-building', label: t('nav.accounts'), page: PANEL_PAGES.Accounts },
  { to: { name: 'list', params: { entityPath: 'opportunities' } }, icon: 'fa-sack-dollar', label: t('nav.opportunities'), page: PANEL_PAGES.Opportunities },
  { to: { name: 'list', params: { entityPath: 'activities' } }, icon: 'fa-calendar-check', label: t('nav.activities'), page: PANEL_PAGES.Activities },
].filter(allowed))
const visibleSections = computed(() => sections.value.filter((s) => s.items.some(allowed)))
function active(item) {
  if (item.path) return route.path === item.path || route.path.startsWith(`${item.path}/`)
  const target = router.resolve(item.to).path
  return item.exact ? route.path === target : route.path.startsWith(target)
}

const quickItems = computed(() => ['account', 'contact', 'lead', 'opportunity', 'activity', 'note']
  .filter((e) => session.has(`${e}:create`))
  .map((e) => ({ label: t(`entities.${e}.new`), icon: { account: 'fa-building', contact: 'fa-user', lead: 'fa-bullseye', opportunity: 'fa-sack-dollar', activity: 'fa-calendar-plus', note: 'fa-note-sticky' }[e], action: () => (ui.openCreate({ entity: e })) })))

const theme = ref(localStorage.getItem('addcrm:theme') || 'system')
function setTheme(value) {
  theme.value = value
  try {
    localStorage.setItem('addcrm:theme', value)
  } catch {
    // theme for this session only
  }
  window.dispatchEvent(new Event('addcrm:theme'))
}

async function switchLanguage() {
  const next = (session.locale === 'en' ? 'pl' : 'en')
  setLocale(next)
  await session.updateSettings({ language: next }).catch(() => null)
}

async function signOut({ wipe = false } = {}) {
  if (wipe) {
    const n = offline.state.pending + offline.state.conflicts + offline.state.failed
    const ok = await ui.ask({ title: t('auth.hardSignOut'), message: t('auth.hardSignOutConfirm', { n }), danger: true, confirmLabel: t('nav.signOut') })
    if (!ok) return
  }
  disconnectEcho()
  offline.stop()
  forgetPermissionCache()
  pages.reset?.()
  await session.forget({ wipe })
  router.push({ name: 'openid-logout' })
}

const userMenu = computed(() => [
  { label: t('nav.profile'), icon: 'fa-id-badge', to: { name: 'profile' } },
  { label: t('nav.sync'), icon: 'fa-rotate', to: { name: 'sync' } },
  { divider: true },
  { label: session.locale === 'en' ? 'Polski' : 'English', icon: 'fa-language', action: switchLanguage },
  { label: theme.value === 'dark' ? 'Light' : 'Dark', icon: theme.value === 'dark' ? 'fa-sun' : 'fa-moon', action: () => setTheme(theme.value === 'dark' ? 'light' : 'dark') },
  // The text's size a pinch left (D-233): back to the usual one.
  ...(textZoom.value !== 1 ? [{ label: t('nav.textZoomReset', { n: Math.round(textZoom.value * 100) }), icon: 'fa-text-height', testid: 'text-zoom-reset', action: () => setTextZoom(1) }] : []),
  { divider: true },
  { label: t('nav.signOut'), icon: 'fa-right-from-bracket', action: () => signOut() },
  { label: t('auth.hardSignOut'), icon: 'fa-eraser', danger: true, action: () => signOut({ wipe: true }) },
])

// The menu narrowed to its icons on a wide screen (a phone's slides in whole): this person's choice on this device.
const narrow = ref(false)
try {
  narrow.value = localStorage.getItem('addcrm:navNarrow') === '1'
} catch {
  // private window: the menu as it is
}
function toggleNarrow() {
  narrow.value = !narrow.value
  try {
    localStorage.setItem('addcrm:navNarrow', narrow.value ? '1' : '0')
  } catch {
    // for this session only
  }
}

const staleText = computed(() => (offline.state.lastPullAt ? formatRelative(offline.state.lastPullAt) : t('sync.never')))

onMounted(async () => {
  // Photos and films of notes kept on this device go out when they can (D-143).
  startNoteUploads(offline)
  // The speech model for dictating without network, fetched once on a good connection (D-144).
  if (!navigator.webdriver) prefetchLocalSpeech()
  flushLearning()
  window.addEventListener('online', () => flushLearning())
  useDirectory().load().catch(() => null)
  useViews().load().catch(() => null)
  offline.start({ appVersion: import.meta.env.VITE_APP_VERSION || 'dev' }).catch(() => null)
  // The files kept for work offline (D-332) not cleared by the browser when space runs short.
  navigator.storage?.persist?.().catch(() => null)
  if (session.mode === 'online') connectEcho(session.me)
})
watch(() => session.mode, (mode) => {
  if (mode === 'online') connectEcho(session.me)
})
watch(() => offline.state.online, async (online) => {
  if (online && session.mode === 'offline') await session.load({ force: true }).catch(() => null)
})
const closeMenuOnRoute = watch(() => route.fullPath, () => (ui.menuOpen = false))
onBeforeUnmount(() => closeMenuOnRoute())

// A pinch sizes the text (D-233): its size shown while the fingers move, and a moment after.
const zoomShown = ref(false)
let zoomHide = 0
const stopPinch = installPinchZoom((_zoom, done) => {
  zoomShown.value = true
  clearTimeout(zoomHide)
  if (done) zoomHide = setTimeout(() => (zoomShown.value = false), 1200)
})
onBeforeUnmount(() => {
  stopPinch()
  clearTimeout(zoomHide)
})
</script>

<template>
  <div class="flex h-full min-h-0">
    <!-- sidebar -->
    <div v-if="ui.menuOpen" class="fixed inset-0 z-30 bg-slate-900/40 lg:hidden" @click="ui.menuOpen = false"></div>
    <aside :class="['fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-default surface transition-[transform,width] lg:static lg:translate-x-0', ui.menuOpen ? 'translate-x-0' : '-translate-x-full', narrow ? 'lg:w-16' : '']" :aria-label="$t('nav.menu')" :data-narrow="narrow" data-testid="app-sidebar" data-privacy-public>
      <router-link :to="{ name: 'dashboard' }" :class="['flex items-center gap-2.5 px-5 py-4', narrow ? 'lg:justify-center lg:px-0' : '']">
        <!-- The logo, light or dark with the theme; narrowed, the sign alone. -->
        <img src="/icons/logo.svg" alt="AddCRM" :class="['-my-1 h-10 dark:hidden', narrow ? 'lg:hidden' : '']" data-testid="app-logo" />
        <img src="/icons/logo-dark.svg" alt="AddCRM" :class="['-my-1 hidden h-10 dark:block', narrow ? 'lg:dark:hidden' : '']" />
        <img v-if="narrow" src="/icons/icon.svg" alt="AddCRM" class="hidden h-8 w-8 lg:block" />
      </router-link>
      <nav :class="['flex-1 overflow-y-auto px-3 pb-4', narrow ? 'lg:px-2' : '']" :data-ready="pages.loaded && !!session.me">
        <div v-for="section in visibleSections" :key="section.title" class="mb-4">
          <p :class="['px-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-faint', narrow ? 'lg:hidden' : '']">{{ section.title }}</p>
          <!-- narrowed: a line between the sections instead of their names -->
          <hr v-if="narrow" class="mx-2 mb-2 hidden border-default lg:block" />
          <template v-for="item in section.items" :key="item.label">
            <router-link
              v-if="allowed(item)"
              :to="item.to"
              :title="narrow ? item.label : undefined"
              :aria-label="item.label"
              :class="['flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors', narrow ? 'lg:justify-center lg:px-0' : '', active(item) ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200' : 'text-muted hover:bg-[var(--surface-3)] hover:text-[var(--text)]']"
            >
              <i :class="['fa-solid fa-fw', item.icon]" aria-hidden="true"></i><span :class="['truncate', narrow ? 'lg:hidden' : '']">{{ item.label }}</span>
            </router-link>
          </template>
        </div>
      </nav>
      <div :class="['flex items-center gap-2 border-t border-default px-4 py-2 text-xs text-faint', narrow ? 'lg:justify-center lg:px-0' : '']">
        <p :class="['min-w-0 flex-1 truncate', narrow ? 'lg:hidden' : '']">{{ session.me?.tenant?.name }}</p>
        <button type="button" class="hidden rounded-lg p-2 text-muted hover:bg-[var(--surface-3)] hover:text-[var(--text)] lg:inline-flex" :title="narrow ? $t('nav.widen') : $t('nav.narrow')" :aria-label="narrow ? $t('nav.widen') : $t('nav.narrow')" :aria-expanded="!narrow" data-testid="sidebar-toggle" @click="toggleNarrow">
          <i :class="['fa-solid fa-fw', narrow ? 'fa-angles-right' : 'fa-angles-left']" aria-hidden="true"></i>
        </button>
      </div>
    </aside>

    <!-- main column -->
    <div class="flex min-w-0 flex-1 flex-col">
      <UpdatePrompt />
      <header class="surface sticky top-0 z-20 flex items-center gap-2 border-b border-default px-3 py-2 sm:gap-3 sm:px-4">
        <button type="button" class="rounded-lg p-2 text-muted hover:bg-[var(--surface-3)] lg:hidden" :aria-label="$t('nav.menu')" @click="ui.menuOpen = true"><i class="fa-solid fa-bars" aria-hidden="true"></i></button>
        <GlobalSearch class="min-w-0 flex-1" />
        <UiMenu v-if="quickItems.length" :items="quickItems" icon="fa-plus" :label="''" button-class="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-white hover:bg-brand-700" variant="none" :aria-label="$t('nav.quickCreate')" data-testid="quick-create" data-privacy-public />
        <span class="flex items-center gap-2 sm:gap-3" data-privacy-public>
          <PrivacyToggle />
          <SyncIndicator />
        </span>
        <AppSwitcher v-if="session.mode === 'online'" class="hidden sm:block" :messages="{ title: $t('nav.apps'), trigger: $t('nav.apps') }" />
        <UiMenu :items="userMenu" button-class="rounded-full" variant="none" data-privacy-public>
          <template #button><UiAvatar :label="session.me?.name || ''" size="md" /></template>
        </UiMenu>
      </header>

      <UiNotice v-if="!offline.state.online" tone="warning" icon="fa-plane" class="m-3 mb-0">{{ $t('messages.offline') }}</UiNotice>
      <!-- Old data is said to be old offline too, where it is edited (D-81): not hidden by the offline notice. -->
      <UiNotice v-if="offline.state.stale" tone="warning" class="m-3 mb-0" data-testid="stale-notice">{{ $t('messages.stale', { when: staleText }) }}</UiNotice>
      <UiNotice v-if="offline.state.needsLogin" tone="error" class="m-3 mb-0">{{ $t('offline.needsLogin') }}</UiNotice>
      <UiNotice v-if="offline.state.foreignQueue" tone="warning" class="m-3 mb-0">{{ $t('auth.foreignQueue') }}</UiNotice>
      <UiNotice v-if="offline.state.revoked" tone="error" class="m-3 mb-0">{{ $t('offline.revoked') }}</UiNotice>

      <main :class="['relative min-h-0 flex-1 overflow-y-auto', route.meta.wide ? '' : 'px-3 py-4 sm:px-6 sm:py-5']">
        <slot />
      </main>
      <UnsavedBar />

      <!-- phone navigation -->
      <nav class="surface flex border-t border-default lg:hidden safe-bottom" :aria-label="$t('nav.menu')" data-privacy-public>
        <router-link v-for="item in phoneItems" :key="item.label" :to="item.to" class="flex flex-1 flex-col items-center gap-0.5 py-2 text-2xs text-muted" active-class="text-brand-600">
          <i :class="['fa-solid', item.icon, 'text-base']" aria-hidden="true"></i>{{ item.label }}
        </router-link>
        <button type="button" class="flex flex-1 flex-col items-center gap-0.5 py-2 text-2xs text-muted" @click="ui.menuOpen = true">
          <i class="fa-solid fa-bars text-base" aria-hidden="true"></i>{{ $t('common.more') }}
        </button>
      </nav>
    </div>
    <CreatePanels />
    <div v-if="zoomShown" class="pointer-events-none fixed left-1/2 top-3 z-[1001] -translate-x-1/2 whitespace-nowrap rounded-full bg-gray-900/85 px-3 py-1.5 text-[14px] font-medium text-white shadow-lg" role="status" data-testid="text-zoom">
      <i class="fa-solid fa-text-height" aria-hidden="true"></i> {{ $t('nav.textZoom', { n: Math.round(textZoom * 100) }) }}
    </div>
  </div>
</template>
